import { apiCall } from './apiUtil';
import { cricketGameAPI, basketballScoringAPI, pickleballGameAPI, tennisGameAPI, pingpongGameAPI, pokerGameAPI, badmintonGameAPI } from '../scoring';

// Note: userAPI has been migrated to /src/profile/services/
// - authApi.js for authentication (login, register, changePassword)
// - profileApi.js for profile operations (getUserById, update, preferences)
// See /src/profile/services/README.md for migration guide

// Note: teamAPI and teammatesAPI have been migrated to /src/teams/services/
// - teamApi.js for team operations (create, update, members, etc.)
// - teammatesApi.js for friend/teammate operations
// See /src/teams/services/index.js for exports

export const tournamentAPI = {
  create: async (tournamentData) => {
    const payload = {
      tournamentName: tournamentData.tournamentName,
      description: tournamentData.description,
      startDate: tournamentData.startDate,
      endDate: tournamentData.endDate,
      registrationDeadline: tournamentData.registrationDeadline || null,
      maxParticipants: tournamentData.maxParticipants,
      registrationFee: tournamentData.registrationFee,
      participantsType: tournamentData.participantsType,
      sportTypeId: tournamentData.sportTypeId ?? null,
      facilityId: tournamentData.facilityId ?? null,
      organizerUserId: tournamentData.organizerUserId ?? null,
      difficultyLevel: tournamentData.difficultyLevel,
      tournamentImage: tournamentData.tournamentImage,
      ownerType: tournamentData.ownerType,
      ownerId: tournamentData.ownerId,
      prizeDetail: tournamentData.prizeDetail,
      status: tournamentData.status,
      matchConfiguration: tournamentData.matchConfiguration,
      playNow: tournamentData.playNow,
    };

    return apiCall('/tournaments', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id) => {
    return apiCall(`/tournaments/${id}`);
  },

  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    if (filters.sportTypeId) queryParams.append('sportTypeId', filters.sportTypeId);
    if (filters.facilityId) queryParams.append('facilityId', filters.facilityId);
    if (filters.organizerUserId) queryParams.append('organizerUserId', filters.organizerUserId);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.difficultyLevel) queryParams.append('difficultyLevel', filters.difficultyLevel);
    if (filters.ownerType) queryParams.append('ownerType', filters.ownerType);
    if (filters.ownerId) queryParams.append('ownerId', filters.ownerId);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/tournaments?${queryString}` : '/tournaments';

    return apiCall(endpoint);
  },

  getMyTournaments: async (organizerUserId) => {
    return apiCall(`/tournaments?organizerUserId=${organizerUserId}`);
  },

  update: async (id, tournamentData) => {
    return apiCall(`/tournaments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(tournamentData),
    });
  },

  delete: async (id) => {
    return apiCall(`/tournaments/${id}`, {
      method: 'DELETE',
    });
  },

  generateSchedule: async (id, options = {}) => {
    return apiCall(`/tournaments/${id}/generate-schedule`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },

  deleteSchedule: async (id) => {
    return apiCall(`/tournaments/${id}/schedule`, {
      method: 'DELETE',
    });
  },
};

export const tournamentScheduleAPI = {
  // Get all games grouped by round
  getRounds: async (tournamentId) => {
    return apiCall(`/tournaments/${tournamentId}/rounds`);
  },

  // Get games in a specific round number
  getRound: async (tournamentId, roundNumber) => {
    return apiCall(`/tournaments/${tournamentId}/rounds/${roundNumber}`);
  },

  // Get all games (flat list) with optional filters
  getGames: async (tournamentId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.roundNumber) queryParams.append('roundNumber', filters.roundNumber);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.groupName) queryParams.append('groupName', filters.groupName);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/tournaments/${tournamentId}/games?${queryString}`
      : `/tournaments/${tournamentId}/games`;
    return apiCall(endpoint);
  },

  // Get a single game by ID
  getGame: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}`);
  },

  // Update game (date, venue, status)
  updateGame: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // Record game result
  recordResult: async (gameId, resultData) => {
    return apiCall(`/tournament-rounds/${gameId}/record-result`, {
      method: 'POST',
      body: JSON.stringify(resultData),
    });
  },

  // Check-in a participant for a game
  checkIn: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}/checkin`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Remove check-in (undo)
  removeCheckIn: async (gameId, data) => {
    return apiCall(`/tournament-rounds/${gameId}/checkin`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  },

  // Get check-ins for a game
  getCheckIns: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}/checkins`);
  },

  // Start a game (requires both participants checked in)
  startGame: async (gameId) => {
    return apiCall(`/tournament-rounds/${gameId}/start`, {
      method: 'POST',
    });
  },

  // Legacy aliases for backward compatibility
  getMatches: async (tournamentId, filters = {}) => {
    return tournamentScheduleAPI.getGames(tournamentId, {
      roundNumber: filters.roundId,
      status: filters.status
    });
  },
  getMatch: async (matchId) => tournamentScheduleAPI.getGame(matchId),
  updateMatch: async (matchId, data) => tournamentScheduleAPI.updateGame(matchId, data),
};

export const tournamentStandingsAPI = {
  getByTournament: async (tournamentId, filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.groupName) queryParams.append('groupName', filters.groupName);
    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/tournaments/${tournamentId}/standings?${queryString}`
      : `/tournaments/${tournamentId}/standings`;
    return apiCall(endpoint);
  },

  recalculate: async (tournamentId) => {
    return apiCall(`/tournament-standings/${tournamentId}/recalculate`, {
      method: 'POST',
    });
  },
};

const SPORT_TYPES_CACHE_KEY = 'cache:sport-types-v2';
const SPORT_TYPES_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export const sportTypeAPI = {
  getAll: async () => {
    try {
      const raw = localStorage.getItem(SPORT_TYPES_CACHE_KEY);
      if (raw) {
        const { data, cachedAt } = JSON.parse(raw);
        if (Array.isArray(data) && Date.now() - cachedAt < SPORT_TYPES_CACHE_TTL_MS) {
          return data;
        }
        // Invalid or expired — clear it
        localStorage.removeItem(SPORT_TYPES_CACHE_KEY);
      }
    } catch {
      // corrupted cache — fall through to fetch
      localStorage.removeItem(SPORT_TYPES_CACHE_KEY);
    }

    const raw = await apiCall('/sport-types');
    // Guarantee an array regardless of what the transport layer resolved —
    // callers render this straight into .map() for dropdowns, and several
    // don't validate array-ness themselves (see apiUtil.js's isCapacitor
    // fetch path for one concrete way this could resolve to a non-array).
    const data = Array.isArray(raw) ? raw : [];
    try {
      localStorage.setItem(SPORT_TYPES_CACHE_KEY, JSON.stringify({ data, cachedAt: Date.now() }));
    } catch {
      // storage full or unavailable — ignore
    }
    return data;
  },

  getById: async (id) => {
    // Use cached getAll data if available to avoid extra request
    try {
      const raw = localStorage.getItem(SPORT_TYPES_CACHE_KEY);
      if (raw) {
        const { data, cachedAt } = JSON.parse(raw);
        if (Date.now() - cachedAt < SPORT_TYPES_CACHE_TTL_MS) {
          const found = (Array.isArray(data) ? data : data?.data || []).find(s => s.id === id || s.id === Number(id));
          if (found) return found;
        }
      }
    } catch {
      // fall through
    }
    return apiCall(`/sport-types/${id}`);
  },

  clearCache: () => {
    localStorage.removeItem(SPORT_TYPES_CACHE_KEY);
  },
};

// facilityAPI moved to /facilities/services/facilityApi.js

export const notificationAPI = {
  create: async (notificationData) => {
    // Use GENERIC_NOTIFICATION template for direct message notifications
    const payload = {
      notification_id: notificationData.notificationId || 'GENERIC_NOTIFICATION',
      data: {
        message: notificationData.message,
        ...notificationData.data
      },
      user_id: notificationData.userId,
      web_link: notificationData.webLink || null,
      priority: notificationData.priority || 'normal',
      created_by: notificationData.createdBy || 1
    };

    return apiCall('/notifications/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getById: async (id) => {
    return apiCall(`/notifications/${id}`);
  },

  update: async (id, notificationData) => {
    return apiCall(`/notifications/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(notificationData),
    });
  },
};

export const tournamentParticipantAPI = {
  register: async (participantData) => {
    // Validate: must have either teamId or userId
    if (!participantData.teamId && !participantData.userId) {
      throw new Error('Registration requires either a team or user ID');
    }

    // The API expects either participant_id OR team_id, not both
    const payload = {
      tournamentId: participantData.tournamentId,
      ...(participantData.teamId
        ? { teamId: participantData.teamId }
        : { participantId: participantData.userId }
      ),
      ...(participantData.roles && {
        coowner: participantData.roles.coowner || false,
        player: participantData.roles.player || false,
        coach: participantData.roles.coach || false,
        parents: participantData.roles.parents || false,
        teamOwner: participantData.roles.teamOwner || false,
        referee: participantData.roles.referee || false,
        scorer: participantData.roles.scorer || false
      }),
      isActive: true
    };

    return apiCall('/tournament-participants', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getByTournamentId: async (tournamentId, page = 1, limit = 10) => {
    const queryParams = new URLSearchParams({
      tournamentId: tournamentId.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    return apiCall(`/tournament-participants?${queryParams.toString()}`);
  },

  getByUserId: async (userId) => {
    return apiCall(`/tournament-participants?participantId=${userId}`);
  },

  getById: async (id) => {
    return apiCall(`/tournament-participants/${id}`);
  },

  update: async (id, participantData) => {
    return apiCall(`/tournament-participants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(participantData),
    });
  },

  delete: async (id) => {
    return apiCall(`/tournament-participants/${id}`, {
      method: 'DELETE',
    });
  },

  checkRegistration: async (tournamentId, userId, teamIds = []) => {
    // Check individual registration
    const individualCheck = apiCall(`/tournament-participants?tournamentId=${tournamentId}&participantId=${userId}`);

    // Also check team registrations for each of the user's teams
    const teamChecks = teamIds.map(teamId =>
      apiCall(`/tournament-participants?tournamentId=${tournamentId}&teamId=${teamId}`)
    );

    const [individualResult, ...teamResults] = await Promise.all([individualCheck, ...teamChecks]);

    const individualData = individualResult?.data || individualResult || [];
    const teamData = teamResults.flatMap(r => r?.data || r || []);

    // Combine and deduplicate by id
    const allData = [...individualData];
    teamData.forEach(teamReg => {
      if (!allData.some(existing => existing.id === teamReg.id)) {
        allData.push(teamReg);
      }
    });

    return { data: allData };
  },

  updateRegistration: async (registrationId, updateData) => {
    return apiCall(`/tournament-participants/${registrationId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  },

  withdrawRegistration: async (registrationId) => {
    return apiCall(`/tournament-participants/${registrationId}`, {
      method: 'DELETE',
    });
  }
};


export const eventsAPI = {
  getLiveEvents: async () => {
    return apiCall('/events/live');
  },

  getMyGames: async (options = {}) => {
    const queryParams = new URLSearchParams();
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.offset) queryParams.append('offset', options.offset);
    if (options.groupId) queryParams.append('groupId', options.groupId);
    if (options.includeAllGroupGames) queryParams.append('includeAllGroupGames', 'true');

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/events/my-games?${queryString}` : '/events/my-games';
    return apiCall(endpoint);
  },

  getById: async (id) => {
    return apiCall(`/events/${id}`);
  },

  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    if (filters.ownerType) queryParams.append('ownerType', filters.ownerType);
    if (filters.ownerId) queryParams.append('ownerId', filters.ownerId);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/events?${queryString}` : '/events';
    return apiCall(endpoint);
  },

  updateStatus: async (eventId, status) => {
    return apiCall(`/events/${eventId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  },
};

// Search API - Unified search across all entities
export const searchAPI = {
  // Main search endpoint
  search: async (query, options = {}) => {
    const params = new URLSearchParams();
    params.append('q', query);
    if (options.types) params.append('types', options.types);
    if (options.limit) params.append('limit', options.limit);
    if (options.semantic !== undefined) params.append('semantic', options.semantic);
    if (options.keyword !== undefined) params.append('keyword', options.keyword);
    // Location for "near me" searches
    if (options.lat !== undefined) params.append('lat', options.lat);
    if (options.lng !== undefined) params.append('lng', options.lng);
    return apiCall(`/search?${params.toString()}`);
  },

  // Health check for search service
  health: async () => {
    return apiCall('/search/health');
  },

  // Get index statistics
  stats: async () => {
    return apiCall('/search/stats');
  }
};

export const teammatesAPI = {
  // Get user's teammates (friends)
  getAll: async (page = 1, limit = 100) => {
    return apiCall(`/teammates?page=${page}&limit=${limit}`);
  },

  // Get teammate status with another user
  getStatus: async (userId) => {
    return apiCall(`/teammates/status/${userId}`);
  },

  // Send a teammate request
  sendRequest: async (receiverId, message = null) => {
    return apiCall('/teammates/request', {
      method: 'POST',
      body: JSON.stringify({ receiverId, message }),
    });
  },

  // Accept a teammate request
  acceptRequest: async (requestId) => {
    return apiCall(`/teammates/request/${requestId}/accept`, {
      method: 'POST',
    });
  },

  // Decline a teammate request
  declineRequest: async (requestId) => {
    return apiCall(`/teammates/request/${requestId}/decline`, {
      method: 'POST',
    });
  },

  // Remove a teammate
  remove: async (userId) => {
    return apiCall(`/teammates/${userId}`, {
      method: 'DELETE',
    });
  },
};

export const academyAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.tab)         qs.set('tab', params.tab);
    if (params.search)      qs.set('search', params.search);
    if (params.sportTypeId) qs.set('sportTypeId', params.sportTypeId);
    if (params.page)        qs.set('page', params.page);
    if (params.limit)       qs.set('limit', params.limit);
    if (params.lat != null) qs.set('lat', params.lat);
    if (params.lng != null) qs.set('lng', params.lng);
    if (params.radiusKm)    qs.set('radiusKm', params.radiusKm);
    // Owners only, and only on my-academies — the API enforces both. Sent as a
    // string because URLSearchParams stringifies `false` to "false", which is
    // truthy on the wire; only send it when it is actually wanted.
    if (params.includeArchived) qs.set('includeArchived', 'true');
    return apiCall(`/academies?${qs.toString()}`);
  },

  getById: async (id) => apiCall(`/academies/${id}`),

  // Everything scheduled under the academy — program class sessions and
  // tournament fixtures — already merged and ordered by the API.
  schedule: async (id, { startDate, endDate } = {}) => {
    const qs = new URLSearchParams();
    if (startDate) qs.set('startDate', startDate);
    if (endDate)   qs.set('endDate', endDate);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiCall(`/academies/${id}/schedule${suffix}`);
  },

  create: async (data) => apiCall('/academies', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: async (id, data) => apiCall(`/academies/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),

  /**
   * Duplicate an academy's setup into a new one of its owner's naming.
   *
   * `parts` says what to bring — programs, tournaments, courts, courtEvents,
   * featureFlags, staff, gallery — and anything left out is left behind.
   * Members, bookings, orders and money never come, whatever is asked for.
   */
  copy: async (id, { name, parts }) => apiCall(`/academies/${id}/copy`, {
    method: 'POST',
    body: JSON.stringify({ name, parts }),
  }),

  /** Put an academy away: visible to its owner, gone for everyone else. */
  archive:   async (id) => apiCall(`/academies/${id}/archive`, { method: 'POST' }),
  unarchive: async (id) => apiCall(`/academies/${id}/archive`, { method: 'DELETE' }),

  staff: {
    list: async (academyId) => apiCall(`/academies/${academyId}/staff`),

    add: async (academyId, data) => apiCall(`/academies/${academyId}/staff`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    updateRole: async (academyId, staffId, data) => apiCall(`/academies/${academyId}/staff/${staffId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

    remove: async (academyId, staffId) => apiCall(`/academies/${academyId}/staff/${staffId}`, {
      method: 'DELETE',
    }),

    // Fine-grained capability grants for non-admin staff (a coach given
    // manage_programs without being promoted to role='admin'). Both return
    // the staff member's full updated capabilities list.
    grantCapability: async (academyId, staffId, capability) =>
      apiCall(`/academies/${academyId}/staff/${staffId}/capabilities/${capability}`, { method: 'POST' }),

    revokeCapability: async (academyId, staffId, capability) =>
      apiCall(`/academies/${academyId}/staff/${staffId}/capabilities/${capability}`, { method: 'DELETE' }),
  },

  openPositions: {
    list: async (academyId) => apiCall(`/academies/${academyId}/open-positions`),

    create: async (academyId, data) => apiCall(`/academies/${academyId}/open-positions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    update: async (academyId, positionId, data) => apiCall(`/academies/${academyId}/open-positions/${positionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

    remove: async (academyId, positionId) => apiCall(`/academies/${academyId}/open-positions/${positionId}`, {
      method: 'DELETE',
    }),
  },

  courts: {
    list: async (academyId) => apiCall(`/academies/${academyId}/courts`),

    /**
     * A court's own diary: the hours it is open, the hours it is closed, and
     * everything holding it — a member's hire and a session's derived hold
     * alike, because to a court they are the same thing.
     */
    schedule: async (academyId, courtId, from, to) =>
      apiCall(`/academies/${academyId}/courts/${courtId}/schedule`
        + `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

    /**
     * What is free on every bookable court, for one date. Public.
     *
     * One call for the whole academy: a booking page listing thirty courts
     * would otherwise be thirty round trips to draw one screen, and changing
     * the date is the first thing anybody does on it.
     */
    dayAvailability: async (academyId, from, to = from) =>
      apiCall(`/academies/${academyId}/courts-availability?from=${from}&to=${to}`, { silent: true }),

    /** Open a court for hire, or close it. Windows are sent already expanded. */
    addWindow: async (academyId, courtId, data) =>
      apiCall(`/academies/${academyId}/courts/${courtId}/schedule`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    removeWindow: async (academyId, courtId, windowId) =>
      apiCall(`/academies/${academyId}/courts/${courtId}/schedule/${windowId}`, { method: 'DELETE' }),

    // Public/unauth listing for a published academy (KYN-536 Gap E) — no JWT or
    // Academies license required, unlike list() above.
    listPublic: async (academyId) => apiCall(`/academies/public/${academyId}/courts`, { silent: true }),

    create: async (academyId, data) => apiCall(`/academies/${academyId}/courts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    update: async (academyId, courtId, data) => apiCall(`/academies/${academyId}/courts/${courtId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

    remove: async (academyId, courtId) => apiCall(`/academies/${academyId}/courts/${courtId}`, {
      method: 'DELETE',
    }),

    // Public (unauth-safe — apiCall only attaches a token if one exists) availability
    // for a date range. Contract: WS2 (KYN-511 court-booking backend).
    availability: async (academyId, courtId, { startDate, endDate } = {}) => {
      const qs = new URLSearchParams();
      if (startDate) qs.set('startDate', startDate);
      if (endDate) qs.set('endDate', endDate);
      const query = qs.toString();
      return apiCall(`/academies/${academyId}/courts/${courtId}/availability${query ? `?${query}` : ''}`);
    },

    book: async (academyId, courtId, data) => apiCall(`/academies/${academyId}/courts/${courtId}/bookings`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    listBookings: async (academyId, courtId, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/academies/${academyId}/courts/${courtId}/bookings${qs ? `?${qs}` : ''}`);
    },

    cancelBooking: async (academyId, courtId, bookingId) => apiCall(`/academies/${academyId}/courts/${courtId}/bookings/${bookingId}`, {
      method: 'DELETE',
    }),

    /**
     * Every court booking the caller has made, across academies. The server
     * already had this (GET /bookings/me) — nothing on the web ever called it,
     * so a player could book a court and then had nowhere to see it.
     */
    myBookings: async () => apiCall('/bookings/me'),
  },

  programs: {
    /**
     * One page of programmes: { data, pagination, filters: { sports } }.
     *
     * Searching and filtering are the server's job here, not the caller's — an
     * academy can have hundreds of offerings, and filtering a single page in
     * the browser would silently miss every match on the pages it is not
     * holding. Callers that want the whole list must page through it.
     */
    list: async (academyId, params = {}) => {
      const qs = new URLSearchParams();
      if (params.page)     qs.set('page', params.page);
      if (params.limit)    qs.set('limit', params.limit);
      if (params.search)   qs.set('search', params.search);
      // 'all' is the dropdowns' resting value and means no filter, so it is
      // not worth a round trip's worth of query string.
      if (params.level && params.level !== 'all') qs.set('level', params.level);
      if (params.sport && params.sport !== 'all') qs.set('sport', params.sport);
      if (params.showPast) qs.set('showPast', 'true');
      const q = qs.toString();
      return apiCall(`/academies/${academyId}/programs${q ? `?${q}` : ''}`);
    },

    create: async (academyId, data) => apiCall(`/academies/${academyId}/programs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    update: async (academyId, programId, data) => apiCall(`/academies/${academyId}/programs/${programId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

    remove: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}`, {
      method: 'DELETE',
    }),

    generateFlyer: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}/flyer/generate`, {
      method: 'POST',
    }),

    saveFlyer: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}/flyer/save`, {
      method: 'POST',
    }),

    // Registration — records the additional-information answers and the
    // terms/waiver acceptance alongside the enrollment.
    register: async (academyId, programId, data) => apiCall(`/academies/${academyId}/programs/${programId}/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    // Terms/waiver documents (S3-backed, same shape as tournaments)
    docTemplate: async (type) => apiCall(`/academies/programs/document-templates/${type}`),

    generateDoc: async (academyId, programId, data) => apiCall(`/academies/${academyId}/programs/${programId}/documents/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

    getDoc: async (academyId, programId, type) => apiCall(`/academies/${academyId}/programs/${programId}/documents/${type}`),

    // In-progress registration, so an interrupted user can pick up where they left off.
    // Which of the caller's own circle is already enrolled — used to stop the
    // registrant picker offering someone who'd be rejected on submit.
    myRegistrations: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}/my-registrations`),

    // Same, across every program in the academy — one call for the programs list.
    myAcademyRegistrations: async (academyId) => apiCall(`/academies/${academyId}/my-registrations`),

    getDraft: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}/registration-draft`),

    saveDraft: async (academyId, programId, data) => apiCall(`/academies/${academyId}/programs/${programId}/registration-draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

    discardDraft: async (academyId, programId) => apiCall(`/academies/${academyId}/programs/${programId}/registration-draft`, {
      method: 'DELETE',
    }),

  },

  members: {
    list: async (academyId, params = {}) => {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.page)   qs.set('page', params.page);
      if (params.limit)  qs.set('limit', params.limit);
      const query = qs.toString();
      return apiCall(`/academies/${academyId}/members${query ? `?${query}` : ''}`);
    },

    add: async (academyId, { targetUserId, role = 'member' }) => apiCall(`/academies/${academyId}/members`, {
      method: 'POST',
      body: JSON.stringify({ targetUserId, role }),
    }),

    invite: async (academyId, { firstName, lastName, email }) => apiCall(`/academies/${academyId}/members/invite`, {
      method: 'POST',
      body: JSON.stringify({ firstName, lastName, email }),
    }),

    bulkResendInvite: async (academyId, memberIds) => apiCall(`/academies/${academyId}/members/bulk-resend-invite`, {
      method: 'POST',
      body: JSON.stringify({ memberIds }),
    }),

    bulkAdd: async (academyId, students) => apiCall(`/academies/${academyId}/members/bulk`, {
      method: 'POST',
      body: JSON.stringify({ students }),
    }),

    // Dry-run categorized preview (S3). Pass { file } to parse+classify an uploaded sheet, or
    // { students } to re-classify an already-parsed set (used after S5 inline corrections).
    bulkAnalyze: async (academyId, { file, students } = {}) => {
      if (file) {
        // Raw fetch — do NOT set Content-Type (browser sets multipart boundary automatically)
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`/exportsapi/academies/${academyId}/members/bulk/analyze`, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ message: 'Analyze failed' }));
          throw new Error(err.message || 'Analyze failed');
        }
        return res.json();
      }
      return apiCall(`/academies/${academyId}/members/bulk/analyze`, {
        method: 'POST',
        body: JSON.stringify({ students }),
      });
    },

    // Selective commit (S4) — persists only the uploader-chosen rows. sendInvitations:true defers
    // membership creation to invite-accept instead of adding members immediately.
    bulkCommit: async (academyId, students, sendInvitations) => apiCall(`/academies/${academyId}/members/bulk/commit`, {
      method: 'POST',
      body: JSON.stringify({ students, sendInvitations: !!sendInvitations }),
    }),

    bulkRemove: async (academyId, memberIds) => apiCall(`/academies/${academyId}/members/bulk-remove`, {
      method: 'POST',
      body: JSON.stringify({ memberIds }),
    }),

    bulkSuspend: async (academyId, memberIds, suspended) => apiCall(`/academies/${academyId}/members/bulk-suspend`, {
      method: 'POST',
      body: JSON.stringify({ memberIds, suspended }),
    }),
  },

  enrollments: {
    list: async (academyId, programId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/enrollments`),

    enroll: async (academyId, programId, memberIds) =>
      apiCall(`/academies/${academyId}/programs/${programId}/enrollments`, {
        method: 'POST',
        body: JSON.stringify({ memberIds }),
      }),

    // Member self-service enrollment — enrolls the calling user's own member row,
    // as opposed to `enroll` above which is the admin-only bulk path.
    selfEnroll: async (academyId, programId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/self-enroll`, {
        method: 'POST',
      }),

    unenroll: async (academyId, programId, memberId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/enrollments/${memberId}`, {
        method: 'DELETE',
      }),
  },

  squads: {
    list: async (academyId, programId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/squads`),

    add: async (academyId, programId, groupId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/squads`, {
        method: 'POST',
        body: JSON.stringify({ groupId }),
      }),

    remove: async (academyId, programId, groupId) =>
      apiCall(`/academies/${academyId}/programs/${programId}/squads/${groupId}`, {
        method: 'DELETE',
      }),

    bulkAssign: async (academyId, programId, assignments) =>
      apiCall(`/academies/${academyId}/programs/${programId}/squad-assignments`, {
        method: 'POST',
        body: JSON.stringify({ assignments }),
      }),
  },

  gallery: {
    list: async (academyId) =>
      apiCall(`/academies/${academyId}/gallery`),

    /**
     * Uploads through the image handler's presigned flow — the same route post
     * images take — then records the resulting key against the academy.
     *
     * The previous version posted the file itself to /exportsapi as multipart,
     * with a relative URL and a token read straight from localStorage. Both are
     * wrong on native: the URL resolves against capacitor://localhost and the
     * token lives in Preferences, so gallery upload could not work in the app at
     * all. Going through uploadFile() also means Rekognition screens the file,
     * which this path skipped entirely.
     *
     * Throws ContentPolicyError when moderation rejects the file, and
     * FileTooLargeError before any network call when it is oversized.
     */
    upload: async (academyId, file, caption) => {
      const { uploadFile } = await import('./imageUploadService');
      const { url } = await uploadFile(file, 'academy_gallery');
      return apiCall(`/academies/${academyId}/gallery`, {
        method: 'POST',
        body: JSON.stringify({ url, caption: caption || null }),
      });
    },

    remove: async (academyId, itemId) =>
      apiCall(`/academies/${academyId}/gallery/${itemId}`, { method: 'DELETE' }),

    reorder: async (academyId, orderedIds) =>
      apiCall(`/academies/${academyId}/gallery/reorder`, {
        method: 'PATCH',
        body: JSON.stringify({ orderedIds }),
      }),
  },

  publish: async (academyId) =>
    apiCall(`/academies/${academyId}/publish`, { method: 'POST' }),

  unpublish: async (academyId) =>
    apiCall(`/academies/${academyId}/unpublish`, { method: 'POST' }),

  getDesignConfig: async (academyId) =>
    apiCall(`/academies/${academyId}/design-config`),

  updateDesignConfig: async (academyId, patch) =>
    apiCall(`/academies/${academyId}/design-config`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // What the published public page discloses (currently the staff email/phone
  // switches). The PATCH response adds republishRequired: the live page is a
  // static file, so a saved change only reaches visitors on the next publish.
  getPersonalization: async (academyId) =>
    apiCall(`/academies/${academyId}/personalization`),

  updatePersonalization: async (academyId, patch) =>
    apiCall(`/academies/${academyId}/personalization`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // Staff schedule. There is deliberately no "list every staff member's
  // schedule" call — the server only ever answers for one person at a time, and
  // only for people within the caller's reporting subtree.
  // Sessions of an occurrence-scoped offering (open play, meetups), and the
  // prices attached to them. Prices resolve event → series → programme, so a
  // session's price may come from a level above it.
  sessions: {
    // One search, two placements. Omit academyId for the global feed; pass it
    // to scope the identical component to a single academy.
    // Who is going to one session and who is waiting. Names and avatars only,
    // deliberately not the responses endpoint, which carries emails.
    roster: async (eventId) => apiCall(`/sessions/${eventId}/roster`),

    // Takes the place and, when the session costs something, opens the order
    // that pays for it. Paying is a separate step — see BookSessionModal.
    book: async (eventId, body = {}) => apiCall(`/sessions/${eventId}/book`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

    /**
     * Give a place back. `playerId` releases a managed player's place instead of
     * your own, verified server-side against managed_by.
     *
     * Withdraws an unpaid order. A paid one is left alone and returned in
     * `paidOrders` — the money is a refund decision for the venue, and
     * re-booking the same session reuses that order, so it stands as credit.
     */
    cancelBooking: async (eventId, body = {}) => apiCall(`/sessions/${eventId}/book`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),

    myBooking: async (eventId) => apiCall(`/sessions/${eventId}/booking`),

    discover: async (params = {}) => {
      const qs = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== '' && v != null),
      ).toString();
      return apiCall(`/sessions/discover${qs ? `?${qs}` : ''}`);
    },
    list: async (academyId, programId, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/academies/${academyId}/programs/${programId}/sessions${qs ? `?${qs}` : ''}`);
    },
    create: async (academyId, programId, payload) =>
      apiCall(`/academies/${academyId}/programs/${programId}/sessions`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    cancel: async (academyId, programId, eventId, { series = false } = {}) =>
      apiCall(`/academies/${academyId}/programs/${programId}/sessions/${eventId}?series=${series}`, {
        method: 'DELETE',
      }),
  },



  staffSchedule: {
    // Omit staffId to start at the caller's own node.
    reports: async (academyId, staffId) =>
      apiCall(`/academies/${academyId}/staff-schedule/reports${staffId ? `?staffId=${staffId}` : ''}`),

    forStaff: async (academyId, staffId, from, to) =>
      apiCall(`/academies/${academyId}/staff-schedule/staff/${staffId}?from=${from}&to=${to}`),

    createEntry: async (academyId, payload) =>
      apiCall(`/academies/${academyId}/staff-schedule/entries`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    updateEntry: async (academyId, entryId, payload) =>
      apiCall(`/academies/${academyId}/staff-schedule/entries/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),

    deleteEntry: async (academyId, entryId) =>
      apiCall(`/academies/${academyId}/staff-schedule/entries/${entryId}`, { method: 'DELETE' }),
  },
};


// Ratings & reviews for an individual, academy, or tournament.
export const reviewsAPI = {
  // targetType: 'individual' | 'academy' | 'tournament'
  listForTarget: async (targetType, targetId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.page)  qs.set('page', params.page);
    if (params.limit) qs.set('limit', params.limit);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiCall(`/reviews/${targetType}/${targetId}${suffix}`);
  },

  create: async ({ targetType, targetId, rating, reviewText }) =>
    apiCall('/reviews', {
      method: 'POST',
      body: JSON.stringify({ targetType, targetId, rating, reviewText }),
    }),

  update: async (reviewId, { rating, reviewText }) =>
    apiCall(`/reviews/${reviewId}`, {
      method: 'PATCH',
      body: JSON.stringify({ rating, reviewText }),
    }),

  delete: async (reviewId) =>
    apiCall(`/reviews/${reviewId}`, { method: 'DELETE' }),
};

const apiService = {
  // General APIs (userAPI, teamAPI, and facilityAPI moved to separate modules)
  tournamentAPI,
  tournamentScheduleAPI,
  tournamentStandingsAPI,
  sportTypeAPI,
  notificationAPI,
  tournamentParticipantAPI,
  eventsAPI,
  searchAPI,
  teammatesAPI,
  // Sport-specific APIs
  cricketGameAPI,
  basketballScoringAPI,
  pickleballGameAPI,
  tennisGameAPI,
  pingpongGameAPI,
  pokerGameAPI,
  badmintonGameAPI,
  // Organized by sport
  sports: {
    cricket: cricketGameAPI,
    basketball: basketballScoringAPI,
    pickleball: pickleballGameAPI,
    tennis: tennisGameAPI,
    pingpong: pingpongGameAPI,
    poker: pokerGameAPI,
    badminton: badmintonGameAPI,
  }
};

// Sport-specific API named exports for convenience
export { cricketGameAPI, basketballScoringAPI, pickleballGameAPI, tennisGameAPI, pingpongGameAPI, pokerGameAPI, badmintonGameAPI };

export default apiService;